/**
 * Welche Gruppe eine Seite braucht, und wohin ein Nutzer ohne Zugriff geschickt wird.
 *
 * Aus App.tsx herausgezogen (2026-10-12), damit die Regel ohne React pruefbar ist
 * (`node-app/test/unit_route_access.ts`). Spiegel der API-Seite: `node-app/src/middleware/groupAuth.ts`.
 */

export const FRONTEND_ROUTE_GROUPS: { groupKey: string; pathPrefixes: string[] }[] = [
  {
    groupKey: 'overview',
    pathPrefixes: ['/daily-controlling'],
  },
  {
    groupKey: 'leads_doi',
    pathPrefixes: [
      '/traffic-leads',
      '/sold-leads',
      '/leads/',
      '/doi-evaluation',
      '/doi-evaluation-optin-emails',
      '/doi-evaluation-affiliates',
      '/doi-evaluation-adref',
      '/doi-process-details',
      '/coreg-efficiency',
    ],
  },
  {
    groupKey: 'adserver',
    pathPrefixes: ['/adserver'],
  },
  {
    groupKey: 'email_marketing',
    pathPrefixes: [
      '/mailchimp',
      '/iterable-',
      '/calendar',
      '/newsletter',
      '/sam-timeline',
      '/newsletter-ads-timeline',
      '/newsletter-ad-insertions',
      '/ad-statistics',
      '/etkp-',
      '/email-history',
      '/email-deliverability',
      '/web-push',
      '/dnv-migration',
      '/welcome-campaign',
    ],
  },
  {
    groupKey: 'revenue_vnr',
    pathPrefixes: ['/vnr-revenue', '/vnr/'],
  },
  {
    groupKey: 'fit_products',
    pathPrefixes: ['/plx-orders', '/anfo-statistics', '/ft-lifetime-value', '/auswertungen'],
  },
  {
    groupKey: 'webinars_mailings',
    pathPrefixes: ['/webinar-', '/plx-mailings', '/ad-evaluation'],
  },
  {
    groupKey: 'content_partners',
    pathPrefixes: ['/redaktion', '/push', '/boerse-global/', '/gurupress/'],
  },
  {
    groupKey: 'administration',
    pathPrefixes: ['/admin/', '/settings/'],
  },
  {
    groupKey: 'nfa_external',
    pathPrefixes: ['/nfa-mailings', '/dnv-asset-analysis'],
  },
  {
    groupKey: 'ga4_traffic',
    pathPrefixes: ['/ga4-traffic'],
  },
  {
    groupKey: 'live',
    pathPrefixes: ['/live-admin'],
  },
  {
    groupKey: 'broker_hub',
    pathPrefixes: ['/broker-hub', '/broker-content'],
  },
  {
    groupKey: 'msm_partner',
    pathPrefixes: ['/msm-partner'],
  },
];

export function getGroupForPath(pathname: string): string | null {
  // "/" is always accessible as landing page
  if (pathname === '/' || pathname === '') return 'overview';
  for (const { groupKey, pathPrefixes } of FRONTEND_ROUTE_GROUPS) {
    for (const prefix of pathPrefixes) {
      if (pathname.startsWith(prefix)) return groupKey;
    }
  }
  return null; // unmapped routes are accessible
}

/** Gruppen externer Partner — Nutzer, die NUR solche haben, sehen nur ihre Partnerseite. */
export const PARTNER_GROUPS = ['msm_partner'];
export const PARTNER_HOME: Record<string, string> = { msm_partner: '/msm-partner' };

export function isPartnerOnly(groups: readonly string[] | null | undefined): boolean {
  return !!groups && groups.length > 0 && groups.every((g) => PARTNER_GROUPS.includes(g));
}

/** Startseite fuer einen eingeschraenkten Nutzer: Partnerseite, sonst der erste erlaubte Bereich. */
export function homeFor(groups: readonly string[]): string | null {
  for (const g of groups) if (PARTNER_HOME[g]) return PARTNER_HOME[g];
  for (const { groupKey, pathPrefixes } of FRONTEND_ROUTE_GROUPS) {
    if (groupKey !== 'overview' && groups.includes(groupKey) && pathPrefixes[0]) {
      return pathPrefixes[0].replace(/\/$/, '');
    }
  }
  return null;
}

/**
 * Wohin umleiten (oder null = Seite zeigen). Vollzugriff (null) sieht alles. Reine Partner sehen nur ihre
 * Partnerseite. Andere eingeschraenkte Nutzer landen statt auf einer leeren "/"-Seite auf ihrem ersten
 * erlaubten Bereich (vorher: Navigate auf "/", das sich selbst umleitete und nichts zeigte).
 */
export function resolveRouteRedirect(pathname: string, groups: readonly string[] | null): string | null {
  if (groups === null) return null;
  if (isPartnerOnly(groups)) {
    const home = homeFor(groups);
    return home && !pathname.startsWith(home) ? home : null;
  }
  const groupKey = getGroupForPath(pathname);
  if (groupKey === null || groups.includes(groupKey)) return null;
  const home = homeFor(groups);
  return home && home !== pathname ? home : null;
}
